import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { useContent } from '../../../contexts/ContentContext';
import { useTranslation } from '../../../i18n';
import BankDetailsSection from './BankDetailsSection';

const RSVPFormStepByStep = ({ guestData, onSubmit, isSubmitting }) => {
  const { content } = useContent();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState('info');
  const [formData, setFormData] = useState({
    guestName: guestData?.guestName || '',
    email: '',
    phone: guestData?.phone || '',
    attending: null,
    attendingCeremony: false,
    attendingReception: false,
    numberOfGuests: guestData?.numberOfGuests || 1,
    hasDietRestriction: false,
    dietaryRestrictions: '',
    specialRequests: '',
  });

  const [errors, setErrors] = useState({});

  const ceremonyEvent = content?.rsvp?.events?.[0];
  const receptionEvent = content?.rsvp?.events?.[1];
  const bank = content?.bank;

  // Dynamic step list based on attendance answer
  const activeSteps = formData.attending === true
    ? ['info', 'sessions', 'wishes', 'bank', 'confirm']
    : formData.attending === false
      ? ['info', 'wishes', 'bank', 'confirm']
      : ['info'];

  const currentIdx = activeSteps.indexOf(currentStep);
  const totalSteps = activeSteps.length;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 'info') {
      if (!formData.guestName?.trim()) {
        newErrors.guestName = t('validation.nameRequired');
      }
      if (formData.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('validation.invalidEmail');
      }
      if (!formData.phone?.trim()) {
        newErrors.phone = t('validation.phoneRequired');
      } else if (!/^\+?[\d\s-()]+$/.test(formData.phone)) {
        newErrors.phone = t('validation.invalidPhone');
      }
      if (formData.attending === null) {
        newErrors.attending = t('validation.attendanceRequired');
      }
    }

    if (step === 'sessions') {
      if (!formData.attendingCeremony && !formData.attendingReception) {
        newErrors.sessions = t('validation.selectSession');
      }
      if (formData.hasDietRestriction && !formData.dietaryRestrictions?.trim()) {
        newErrors.dietaryRestrictions = t('validation.dietDetailsRequired');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentIdx < activeSteps.length - 1) {
        setCurrentStep(activeSteps[currentIdx + 1]);
      }
    }
  };

  const handleBack = () => {
    if (currentIdx > 0) {
      setCurrentStep(activeSteps[currentIdx - 1]);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateStep(currentStep)) {
      onSubmit(formData);
    }
  };

  const renderProgressIndicator = () => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-foreground">
          {t('rsvp.step', { current: currentIdx + 1, total: totalSteps })}
        </span>
        <span className="text-sm text-muted-foreground">
          {t('rsvp.complete', { percentage: Math.round(((currentIdx + 1) / totalSteps) * 100) })}
        </span>
      </div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-300 ease-in-out"
          style={{ width: `${((currentIdx + 1) / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 'info':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Icon name="User" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-2">{t('rsvp.yourInfo')}</h3>
              <p className="text-sm text-muted-foreground">{t('rsvp.letUsKnow')}</p>
            </div>
            <Input
              label={t('rsvp.fullName')}
              type="text"
              placeholder={t('rsvp.enterYourName')}
              value={formData.guestName}
              onChange={(e) => handleInputChange('guestName', e.target.value)}
              error={errors.guestName}
              autoFocus
            />
            <Input
              label={t('rsvp.emailAddress')}
              type="email"
              placeholder={t('rsvp.enterEmail')}
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={errors.email}
            />
            <Input
              label={t('rsvp.phoneNumber')}
              type="tel"
              placeholder={t('rsvp.enterPhone')}
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              error={errors.phone}
            />

            {/* Will Attend? */}
            <div className="pt-4">
              <label className="block text-sm font-medium text-foreground mb-3">{t('rsvp.willYouAttend')} *</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleInputChange('attending', true)}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    formData.attending === true
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  <Icon name="Check" size={24} className="mx-auto mb-1" />
                  <div className="font-semibold text-sm">{t('rsvp.yesAttend')}</div>
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange('attending', false)}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    formData.attending === false
                      ? 'border-destructive bg-destructive/10 text-destructive'
                      : 'border-border bg-background text-muted-foreground hover:border-destructive/50'
                  }`}
                >
                  <Icon name="X" size={24} className="mx-auto mb-1" />
                  <div className="font-semibold text-sm">{t('rsvp.noAttend')}</div>
                </button>
              </div>
              {errors.attending && (
                <p className="text-sm text-error flex items-center gap-2 mt-2">
                  <Icon name="AlertCircle" size={16} />
                  {errors.attending}
                </p>
              )}
            </div>
          </div>
        );

      case 'sessions':
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Icon name="Calendar" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-2">{t('rsvp.whichSession')}</h3>
            </div>

            {/* Session checkboxes */}
            <div className="bg-card rounded-xl p-4 border border-border">
              <Checkbox
                id="ceremony"
                label={
                  <div className="ml-3">
                    <div className="font-semibold text-foreground">{ceremonyEvent?.title || t('rsvp.attendCeremony')}</div>
                    {(ceremonyEvent?.time || ceremonyEvent?.venue) && (
                      <div className="text-sm text-muted-foreground mt-1">
                        {ceremonyEvent.time}{ceremonyEvent.time && ceremonyEvent.venue && ' \u2022 '}{ceremonyEvent.venue}
                      </div>
                    )}
                  </div>
                }
                checked={formData.attendingCeremony}
                onChange={(e) => handleInputChange('attendingCeremony', e.target.checked)}
              />
            </div>
            <div className="bg-card rounded-xl p-4 border border-border">
              <Checkbox
                id="reception"
                label={
                  <div className="ml-3">
                    <div className="font-semibold text-foreground">{receptionEvent?.title || t('rsvp.attendReception')}</div>
                    {(receptionEvent?.time || receptionEvent?.venue) && (
                      <div className="text-sm text-muted-foreground mt-1">
                        {receptionEvent.time}{receptionEvent.time && receptionEvent.venue && ' \u2022 '}{receptionEvent.venue}
                      </div>
                    )}
                  </div>
                }
                checked={formData.attendingReception}
                onChange={(e) => handleInputChange('attendingReception', e.target.checked)}
              />
            </div>
            {errors.sessions && (
              <p className="text-sm text-error flex items-center gap-2">
                <Icon name="AlertCircle" size={16} />
                {errors.sessions}
              </p>
            )}

            {/* Companions */}
            <div className="pt-2">
              <label className="block text-sm font-medium text-foreground mb-2">{t('rsvp.totalAttending')}</label>
              <Select
                options={[1, 2, 3, 4, 5].map(n => ({
                  value: n,
                  label: `${n} ${n === 1 ? t('rsvp.person') : t('rsvp.persons')}`
                }))}
                value={formData.numberOfGuests}
                onChange={(value) => handleInputChange('numberOfGuests', parseInt(value, 10))}
              />
            </div>

            {/* Diet Restriction */}
            <div className="pt-2">
              <label className="block text-sm font-medium text-foreground mb-3">{t('rsvp.dietRestriction')}</label>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <button
                  type="button"
                  onClick={() => handleInputChange('hasDietRestriction', true)}
                  className={`p-3 rounded-xl border-2 transition-all text-center font-medium ${
                    formData.hasDietRestriction === true
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  {t('rsvp.dietYes')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handleInputChange('hasDietRestriction', false);
                    handleInputChange('dietaryRestrictions', '');
                  }}
                  className={`p-3 rounded-xl border-2 transition-all text-center font-medium ${
                    formData.hasDietRestriction === false
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-background text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  {t('rsvp.dietNo')}
                </button>
              </div>
              {formData.hasDietRestriction && (
                <textarea
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-romantic"
                  placeholder={t('rsvp.dietPlaceholder')}
                  value={formData.dietaryRestrictions}
                  onChange={(e) => handleInputChange('dietaryRestrictions', e.target.value)}
                  rows={3}
                />
              )}
              {errors.dietaryRestrictions && (
                <p className="text-sm text-error flex items-center gap-2 mt-2">
                  <Icon name="AlertCircle" size={16} />
                  {errors.dietaryRestrictions}
                </p>
              )}
            </div>
          </div>
        );

      case 'wishes':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Icon name="MessageSquare" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-2">{t('rsvp.wishesForCouple')}</h3>
            </div>
            <textarea
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-romantic"
              placeholder={t('rsvp.wishesPlaceholder')}
              value={formData.specialRequests}
              onChange={(e) => handleInputChange('specialRequests', e.target.value)}
              rows={5}
            />
          </div>
        );

      case 'bank':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Icon name="Gift" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-2">{t('bank.weddingGift')}</h3>
            </div>
            <BankDetailsSection bank={bank} />
          </div>
        );

      case 'confirm':
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Icon name="Check" size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground mb-2">{t('rsvp.confirmation')}</h3>
              <p className="text-sm text-muted-foreground">{t('rsvp.reviewYourRsvp')}</p>
            </div>

            <div className="bg-card rounded-xl p-4 border border-border space-y-3">
              <div>
                <div className="text-sm font-medium text-muted-foreground">{t('rsvp.guestName')}</div>
                <div className="text-base text-foreground">{formData.guestName}</div>
              </div>
              {formData.email && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground">{t('rsvp.email')}</div>
                  <div className="text-base text-foreground">{formData.email}</div>
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-muted-foreground">{t('rsvp.phone')}</div>
                <div className="text-base text-foreground">{formData.phone}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground">{t('rsvp.attending')}</div>
                <div className="text-base text-foreground">
                  {formData.attending ? t('rsvp.yesAttend') : t('rsvp.noAttend')}
                </div>
              </div>
              {formData.attending && (
                <>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">{t('rsvp.sessions')}</div>
                    <div className="text-base text-foreground">
                      {formData.attendingCeremony && (ceremonyEvent?.title || t('rsvp.attendCeremony'))}
                      {formData.attendingCeremony && formData.attendingReception && ', '}
                      {formData.attendingReception && (receptionEvent?.title || t('rsvp.attendReception'))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">{t('rsvp.companions')}</div>
                    <div className="text-base text-foreground">
                      {formData.numberOfGuests} {formData.numberOfGuests === 1 ? t('rsvp.person') : t('rsvp.persons')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">{t('rsvp.diet')}</div>
                    <div className="text-base text-foreground">
                      {formData.hasDietRestriction ? formData.dietaryRestrictions : t('rsvp.noDietRestriction')}
                    </div>
                  </div>
                </>
              )}
              {formData.specialRequests && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground">{t('rsvp.wishes')}</div>
                  <div className="text-base text-foreground">{formData.specialRequests}</div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = currentStep === 'confirm';

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
      {renderProgressIndicator()}

      <div className="min-h-[400px] mb-6">
        {renderStep()}
      </div>

      <div className="flex gap-3">
        {currentIdx > 0 && (
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={handleBack}
            iconName="ChevronLeft"
            iconPosition="left"
            className="flex-1"
          >
            {t('rsvp.back')}
          </Button>
        )}

        {!isLastStep ? (
          <Button
            type="button"
            variant="default"
            size="lg"
            onClick={handleNext}
            iconName="ChevronRight"
            iconPosition="right"
            className="flex-1"
          >
            {t('rsvp.next')}
          </Button>
        ) : (
          <Button
            type="submit"
            variant="default"
            size="lg"
            loading={isSubmitting}
            iconName="Check"
            iconPosition="left"
            className="flex-1"
          >
            {t('rsvp.submitRsvp')}
          </Button>
        )}
      </div>
    </form>
  );
};

export default RSVPFormStepByStep;
